import { useGetItem } from '@/api/items/getItem';
import { ItemType } from '@/components/item/itemType';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { Text } from '@/components/ui/typography';
import { cn } from '@/lib/utils';

/**
 * What an item hands out, read from `GET /v1/item/:id`: the list endpoints carry
 * no `bundle_items`. With `quantity` the item itself leads (a gift's grant), its
 * bundle content indented under it; without, only the bundle content shows.
 * Mount it only while open — it fetches on mount.
 */
export const ItemContent = ({
  itemId,
  quantity,
}: {
  itemId: string;
  quantity?: number;
}) => {
  // isLoading, not isPending: without items:read the query never runs.
  const { data: item, isLoading } = useGetItem(itemId);

  if (isLoading) {
    return (
      <div aria-busy className="flex flex-col gap-2.5 rounded-md bg-card p-2.5">
        <Skeleton className="h-3.5 w-2/5" />
        <Skeleton className="h-3.5 w-1/3" />
      </div>
    );
  }
  if (!item) {
    return <Text variant="muted">Could not load this item.</Text>;
  }

  const lines = [
    ...(quantity === undefined ? [] : [{ item, quantity, nested: false }]),
    ...(item.bundle_items ?? []).map((line) => ({
      ...line,
      nested: quantity !== undefined,
    })),
  ];

  return (
    <div className="rounded-md bg-card px-2">
      <Table noWrapper>
        <TableBody>
          {lines.map((line) => (
            <TableRow key={line.item.item_id} className="hover:bg-transparent">
              <TableCell className={cn('py-1.5', line.nested && 'pl-8')}>
                <div className="flex items-center gap-2">
                  <ItemType
                    type={line.item.type}
                    item_rarity={line.item.item_rarity}
                    size={16}
                  />
                  <span className="font-mono">{line.item.code}</span>
                </div>
              </TableCell>
              <TableCell className="w-16 py-1.5 text-right tabular-nums">
                ×{line.quantity}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
